import { useEffect, useState } from "react";
import { refreshAutopilotToken } from "@/lib/helper";
import { NavLink, Outlet } from "react-router-dom";
import { Bot, CalendarClock, CalendarDays, FileText, History, Images, LayoutDashboard, LogOut, Menu, PenLine, Settings, Users, Send, Zap } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import InstallAppButton from "@/components/InstallAppButton";
import { cn } from "@/lib/utils";

const BOTTOM_NAV = [
  { to: "/", label: "Utama", icon: LayoutDashboard, id: "nav-dashboard", end: true },
  { to: "/tulis", label: "Tulis", icon: PenLine, id: "nav-composer" },
  { to: "/post-pantas", label: "Pantas", icon: Zap, id: "nav-fast" },
  { to: "/jadual", label: "Giliran", icon: CalendarClock, id: "nav-queue" },
  { to: "/kumpulan", label: "Group", icon: Users, id: "nav-groups" },
];

const NAV = [
  { to: "/", label: "Papan Pemuka", icon: LayoutDashboard, id: "nav-dashboard", end: true },
  { to: "/tulis", label: "Tulis Post", icon: PenLine, id: "nav-composer" },
  { to: "/jadual", label: "Jadual & Giliran", icon: CalendarClock, id: "nav-queue" },
  { to: "/autopilot", label: "AI Autopilot", icon: Bot, id: "nav-autopilot" },
  { to: "/kalendar", label: "Kalendar", icon: CalendarDays, id: "nav-calendar" },
  { to: "/template", label: "Template", icon: FileText, id: "nav-templates" },
  { to: "/galeri", label: "Galeri AI", icon: Images, id: "nav-gallery" },
  { to: "/kumpulan", label: "Kumpulan FB", icon: Users, id: "nav-groups" },
  { to: "/sejarah", label: "Sejarah", icon: History, id: "nav-history" },
  { to: "/tetapan", label: "Tetapan", icon: Settings, id: "nav-settings" },
];

function Brand() {
  return (
    <div className="flex items-center gap-2.5 px-2">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary shadow-[0_0_24px_-4px_hsl(var(--primary))]">
        <Send className="h-4 w-4 text-white" />
      </div>
      <div>
        <p className="text-[15px] font-extrabold leading-none tracking-tight">PostPintar</p>
        <p className="eyebrow mt-1 !text-[9px]">Automasi Facebook</p>
      </div>
    </div>
  );
}

function NavList({ onNavigate, prefix }) {
  return (
    <nav className="mt-8 flex flex-col gap-1">
      {NAV.map(({ to, label, icon: Icon, id, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          data-testid={`${prefix}-${id}`}
          className={({ isActive }) =>
            cn(
              "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200",
              isActive ? "bg-primary/15 text-white" : "text-muted-foreground hover:bg-accent hover:text-white"
            )
          }
        >
          <Icon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  useEffect(() => { refreshAutopilotToken(); }, []);

  return (
    <div className="relative z-10 flex min-h-screen">
      <aside className="sticky top-0 hidden h-screen w-[260px] shrink-0 flex-col border-r border-border bg-card/40 p-5 md:flex">
        <Brand />
        <NavList prefix="side" />
        <div className="mt-auto space-y-3">
          <InstallAppButton />
          <div className="rounded-lg border border-border p-3">
          <p className="truncate text-xs text-muted-foreground" data-testid="sidebar-user-email">{user?.email}</p>
          <Button variant="ghost" size="sm" className="mt-2 w-full justify-start px-0 text-muted-foreground hover:bg-transparent hover:text-white" onClick={logout} data-testid="logout-btn">
            <LogOut className="mr-2 h-4 w-4" /> Log keluar
          </Button>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-background/70 px-4 backdrop-blur-xl sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 md:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[270px] border-border bg-card p-5">
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <Brand />
                <NavList prefix="mobile" onNavigate={() => setOpen(false)} />
                <InstallAppButton className="mt-6" />
                <Button variant="ghost" size="sm" className="mt-3 justify-start text-muted-foreground" onClick={logout} data-testid="mobile-logout-btn">
                  <LogOut className="mr-2 h-4 w-4" /> Log keluar
                </Button>
              </SheetContent>
            </Sheet>
            <p className="font-extrabold tracking-tight">PostPintar</p>
          </div>
          <p className="eyebrow hidden md:block">Ruang kerja anda</p>
          <NotificationBell />
        </header>
        <main className="mx-auto w-full max-w-6xl flex-1 p-4 pb-28 sm:p-6 sm:pb-28 md:pb-6 lg:p-10">
          <Outlet />
        </main>
        <nav
          className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t border-border bg-card/95 backdrop-blur-xl md:hidden"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          data-testid="mobile-bottom-nav"
        >
          {BOTTOM_NAV.map(({ to, label, icon: Icon, id, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`bottom-${id}`}
              className={({ isActive }) => cn("flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors duration-200", isActive ? "text-primary" : "text-muted-foreground")}
            >
              <Icon className="h-5 w-5" />
              {label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
